import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import axios from 'axios';
import {
  Sunrise,
  Quote as QuoteIcon,
  TrendingUp,
  Target,
  Trophy,
  Sparkles,
  CalendarDays,
  Crown,
  Medal,
  Award,
} from 'lucide-react';

/**
 * MorningBriefingDialog
 * ---------------------
 * Once per IST calendar day, the first time a counselor opens /dashboard we
 * show a motivational briefing: today's quote (deterministic, no LLM), the
 * counselor's last-week / this-week / month-vs-target stats, and the top
 * three performers across the team for the current month.
 *
 * Storage key: `morningBriefing_<userId>_<YYYY-MM-DD-IST>`.
 * Anyone non-counselor (admin / team-lead / manager) sees it too but only
 * if their localStorage flag is unset — keeping the surprise + camaraderie.
 */
export function MorningBriefingDialog({ apiBase, token, user }) {
  const [open, setOpen]       = useState(false);
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!user || !token) return;

    // Compute IST date as YYYY-MM-DD without a timezone library.
    const now = new Date();
    const istMs = now.getTime() + (5 * 60 + 30) * 60 * 1000 + now.getTimezoneOffset() * 60 * 1000;
    const ist   = new Date(istMs);
    const istDate = ist.toISOString().slice(0, 10);

    const key = `morningBriefing_${user.id}_${istDate}`;
    if (localStorage.getItem(key) === 'acknowledged') return;

    // No time-of-day gate: the popup is the first thing a counselor sees
    // on their first dashboard load of the IST day, irrespective of the
    // clock. The 'morning' refers to the start of their workday, not the
    // wall-clock hour. The localStorage acknowledgement guarantees we only
    // surface this once per IST date.

    axios.get(`${apiBase}/morning-briefing`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        setData(res.data);
        setOpen(true);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'Failed to load briefing'));

    // We acknowledge AFTER the user clicks "Let's go" so an accidental tab
    // close doesn't suppress the briefing for the rest of the day.
  }, [apiBase, token, user]);

  const acknowledge = () => {
    if (user && data) {
      const today = data.today_ist;
      const key   = `morningBriefing_${user.id}_${today}`;
      localStorage.setItem(key, 'acknowledged');
    }
    setOpen(false);
  };

  if (!data || error) return null;

  const stats = data.personal_stats || {};
  const top   = data.top_performers || [];

  const weekDelta = (stats.this_week_admissions || 0) - (stats.last_week_admissions || 0);
  const trendUp   = weekDelta > 0;
  const trendFlat = weekDelta === 0;

  const podiumIcon = (i) =>
    i === 0 ? <Crown className="h-5 w-5 text-yellow-500" /> :
    i === 1 ? <Medal className="h-5 w-5 text-gray-400" /> :
              <Award className="h-5 w-5 text-amber-700" />;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) acknowledge(); }}>
      <DialogContent className="sm:max-w-[600px] overflow-hidden p-0" data-testid="morning-briefing-dialog">
        {/* hero gradient header */}
        <div className="relative bg-gradient-to-br from-[#0066CC] via-[#1d4ed8] to-[#7c3aed] text-white px-7 py-6">
          <div className="absolute -top-6 -right-6 h-32 w-32 rounded-full bg-white/10 blur-2xl" />
          <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/10 to-transparent" />
          <DialogHeader className="relative">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-white/80">
              <Sunrise className="h-4 w-4" />
              {data.greeting} briefing
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-normal text-white/70">
                <CalendarDays className="h-3 w-3" />
                {new Date(data.today_ist).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>
            </div>
            <DialogTitle className="font-heading text-2xl leading-tight text-white pt-1" data-testid="morning-briefing-name">
              {data.greeting}, {data.name?.split(' ')[0] || data.name} 👋
            </DialogTitle>
            <DialogDescription className="text-white/85 font-body leading-relaxed">
              Here&apos;s your snapshot to set the tone for the day.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-7 pt-5 pb-2 space-y-5">
          {/* Quote */}
          <div className="rounded-xl border-l-4 border-[#0066CC] bg-[#0066CC]/5 px-4 py-3" data-testid="morning-quote">
            <QuoteIcon className="h-5 w-5 text-[#0066CC] inline-block -mt-1 mr-1" />
            <span className="font-heading text-base text-gray-800 italic">{data.quote.text}</span>
            <div className="mt-1 ml-7 text-xs font-medium text-gray-500">— {data.quote.author}</div>
          </div>

          {/* Personal stats */}
          <div>
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5">
              <TrendingUp className="h-4 w-4 text-[#0066CC]" /> Your performance
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <StatCard
                label="Last week"
                value={stats.last_week_admissions}
                hint="admissions"
                muted
                testid="stat-last-week"
              />
              <StatCard
                label="This week"
                value={stats.this_week_admissions}
                hint={trendFlat ? 'same pace' : (trendUp ? `+${weekDelta} vs last` : `${weekDelta} vs last`)}
                accent={trendUp ? 'green' : (trendFlat ? 'neutral' : 'orange')}
                testid="stat-this-week"
              />
              <StatCard
                label={`${stats.month_name || 'Month'} so far`}
                value={stats.this_month_admissions}
                hint={stats.month_target > 0 ? `of ${stats.month_target} target` : 'no target set'}
                accent="primary"
                testid="stat-month"
              />
            </div>
            {stats.month_target > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs font-medium text-gray-600 mb-1">
                  <span className="inline-flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Monthly target progress</span>
                  <span className="font-semibold text-gray-900" data-testid="stat-progress-pct">{stats.progress_pct}%</span>
                </div>
                <Progress
                  value={Math.min(stats.progress_pct, 100)}
                  className="h-2 bg-gray-200 [&>div]:bg-gradient-to-r [&>div]:from-[#0066CC] [&>div]:to-[#7c3aed]"
                />
                {stats.progress_pct >= 100 && (
                  <p className="mt-2 text-xs font-semibold text-green-600 inline-flex items-center gap-1">
                    <Sparkles className="h-3.5 w-3.5" /> You&apos;ve already hit this month&apos;s target!
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Top performers */}
          <div>
            <h3 className="font-heading text-sm font-semibold uppercase tracking-wider text-gray-500 mb-3 flex items-center gap-1.5">
              <Trophy className="h-4 w-4 text-[#0066CC]" /> Top performers — {stats.month_name}
            </h3>
            {top.length === 0 ? (
              <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
                No admissions logged yet this month. Be the first on the leaderboard!
              </div>
            ) : (
              <ol className="space-y-2" data-testid="top-performers-list">
                {top.map((p, i) => (
                  <li
                    key={p.user_id}
                    className={`flex items-center justify-between rounded-lg px-3 py-2 transition ${
                      p.is_self
                        ? 'bg-gradient-to-r from-[#0066CC]/10 to-[#7c3aed]/10 ring-1 ring-[#0066CC]/30'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {podiumIcon(i)}
                      <div>
                        <div className="font-heading text-sm font-semibold text-gray-900">
                          {p.name}
                          {p.is_self && <Badge className="ml-2 bg-[#0066CC] text-white">You</Badge>}
                        </div>
                        <div className="text-xs text-gray-500">#{i + 1} this month</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-xl font-bold text-gray-900">{p.admissions_count}</div>
                      <div className="text-[10px] uppercase tracking-wider text-gray-400">admissions</div>
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <DialogFooter className="bg-gray-50 px-7 py-4 border-t border-gray-100">
          <Button
            onClick={acknowledge}
            className="w-full sm:w-auto bg-gradient-to-r from-[#0066CC] to-[#7c3aed] hover:opacity-95 text-white font-semibold"
            data-testid="morning-briefing-acknowledge"
          >
            Let&apos;s make today count
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, hint, accent, muted, testid }) {
  const accentBorder = accent === 'green'   ? 'border-green-200'   :
                       accent === 'orange'  ? 'border-orange-200'  :
                       accent === 'primary' ? 'border-[#0066CC]/30' :
                                              'border-gray-200';
  const accentText   = accent === 'green'   ? 'text-green-700'     :
                       accent === 'orange'  ? 'text-orange-700'    :
                       accent === 'primary' ? 'text-[#0066CC]'     :
                                              'text-gray-700';
  return (
    <div className={`rounded-lg border ${accentBorder} bg-white px-3 py-3`} data-testid={testid}>
      <div className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{label}</div>
      <div className={`font-heading text-3xl font-bold mt-0.5 ${muted ? 'text-gray-500' : accentText}`}>{value || 0}</div>
      <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>
    </div>
  );
}
